<template>
  <div class="product-list-view">
    <section class="toolbar-card">
      <div>
        <h2>产品列表 Product List</h2>
        <p>维护产品名称、描述、图片、分类和固定规格字段。</p>
      </div>
      <div class="toolbar-card__actions">
        <el-input
          v-model.trim="keyword"
          clearable
          placeholder="搜索编号/标题 Search code/title"
          size="small"
          @keyup.enter.native="handleFilterChange"
          @clear="handleFilterChange"
        />
        <el-select v-model="categoryId" clearable placeholder="按分类筛选 Filter by category" size="small" @change="handleFilterChange">
          <el-option
            v-for="item in categories"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
        <el-select v-model="statusFilter" clearable placeholder="状态 Status" size="small" @change="handleFilterChange">
          <el-option label="草稿 Draft" value="draft" />
          <el-option label="启用 Active" value="active" />
        </el-select>
        <el-select v-model="colorFilter" clearable filterable placeholder="颜色 Color" size="small" @change="handleFilterChange">
          <el-option v-for="item in colorOptions" :key="item" :label="item" :value="item" />
        </el-select>
        <el-button size="small" @click="handleFilterChange">查询 Search</el-button>
        <el-button type="primary" size="small" @click="openCreateDialog">新增产品 Add Product</el-button>
      </div>
    </section>

    <section class="table-card">
      <el-table :data="products" v-loading="loading" stripe>
        <el-table-column prop="code" label="编号 Code" min-width="120" />
        <el-table-column prop="title" label="名称 Name" min-width="180" />
        <el-table-column label="分类 Category" min-width="180">
          <template slot-scope="{ row }">
            {{ row.category ? row.category.name : '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="color" label="颜色 Color" min-width="150" />
        <el-table-column label="状态 Status" min-width="150">
          <template slot-scope="{ row }">
            <el-switch
              :value="row.status === 'active'"
              active-text="启用 Active"
              inactive-text="草稿 Draft"
              @change="value => toggleProductStatus(row, value)"
            />
          </template>
        </el-table-column>
        <el-table-column label="排序 Sort Order" min-width="150">
          <template slot-scope="{ row }">
            <el-input-number
              class="sort-input"
              :value="row.sortOrder"
              :min="0"
              :step="1"
              size="mini"
              @change="value => updateProductSortOrder(row, value)"
            />
          </template>
        </el-table-column>
        <el-table-column label="图库数 Gallery" min-width="120">
          <template slot-scope="{ row }">
            {{ Array.isArray(row.galleryJson) ? row.galleryJson.length : 0 }}
          </template>
        </el-table-column>
        <el-table-column label="操作 Actions" min-width="250" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" @click="previewProduct(row)">预览 Preview</el-button>
            <el-button type="text" @click="openEditDialog(row)">编辑 Edit</el-button>
            <el-button type="text" :loading="duplicatingId === row.id" @click="handleDuplicate(row)">复制 Copy</el-button>
            <el-button type="text" class="danger-btn" @click="handleDelete(row)">删除 Delete</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination-bar">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next"
          :current-page="pagination.page"
          :page-size="pagination.pageSize"
          :page-sizes="[10, 20, 50]"
          :total="pagination.total"
          @current-change="handlePageChange"
          @size-change="handlePageSizeChange"
        />
      </div>
    </section>

    <el-dialog :title="dialogMode === 'create' ? '新增产品 Add Product' : '编辑产品 Edit Product'" :visible.sync="dialogVisible" width="860px" @closed="resetForm">
      <el-form ref="form" :model="form" :rules="rules" label-position="top">
        <el-tabs v-model="activeProductTab" class="product-form-tabs">
          <el-tab-pane label="基础信息 Basic" name="basic">
            <div class="form-grid">
              <el-form-item label="产品编号 Product Code" prop="code">
                <el-input v-model.trim="form.code" />
              </el-form-item>
              <el-form-item label="产品名称 Product Name" prop="title">
                <el-input v-model.trim="form.title" />
              </el-form-item>
            </div>

            <div class="form-grid">
              <el-form-item label="标识 Slug" prop="slug">
                <el-input v-model.trim="form.slug" @input="handleSlugInput" />
              </el-form-item>
              <el-form-item label="眉题 Eyebrow">
                <el-input v-model.trim="form.eyebrow" />
              </el-form-item>
            </div>

            <div class="form-grid">
              <el-form-item label="分类 Category" prop="categoryId">
                <el-select v-model="form.categoryId" clearable placeholder="请选择 Please select">
                  <el-option
                    v-for="item in categories"
                    :key="item.id"
                    :label="item.name"
                    :value="item.id"
                  />
                </el-select>
              </el-form-item>
              <el-form-item label="状态 Status" prop="status">
                <el-select v-model="form.status" placeholder="请选择 Please select">
                  <el-option label="草稿 Draft" value="draft" />
                  <el-option label="启用 Active" value="active" />
                </el-select>
              </el-form-item>
            </div>

            <el-form-item label="排序 Sort Order">
              <el-input-number v-model="form.sortOrder" :min="0" :step="1" />
            </el-form-item>
          </el-tab-pane>

          <el-tab-pane label="图片 Images" name="images">
            <div class="form-grid">
              <el-form-item label="主图 Main Image">
                <div class="image-field">
                  <el-input v-model.trim="form.image" placeholder="图片地址 Image URL" />
                  <div class="image-field__actions">
                    <el-upload action="" :http-request="request => uploadImage('image', request)" :show-file-list="false" accept="image/*">
                      <el-button size="small" :loading="isUploading('image')">上传 Upload</el-button>
                    </el-upload>
                    <el-button size="small" @click="openMediaPicker('image')">选择 Choose</el-button>
                  </div>
                  <el-image v-if="form.image" class="image-preview" :src="assetUrl(form.image)" :preview-src-list="[assetUrl(form.image)]" fit="cover" />
                </div>
              </el-form-item>
              <el-form-item label="封面图 Cover Image">
                <div class="image-field">
                  <el-input v-model.trim="form.coverImage" placeholder="图片地址 Image URL" />
                  <div class="image-field__actions">
                    <el-upload action="" :http-request="request => uploadImage('coverImage', request)" :show-file-list="false" accept="image/*">
                      <el-button size="small" :loading="isUploading('coverImage')">上传 Upload</el-button>
                    </el-upload>
                    <el-button size="small" @click="openMediaPicker('coverImage')">选择 Choose</el-button>
                  </div>
                  <el-image v-if="form.coverImage" class="image-preview" :src="assetUrl(form.coverImage)" :preview-src-list="[assetUrl(form.coverImage)]" fit="cover" />
                </div>
              </el-form-item>
            </div>

            <el-form-item label="头图 Hero Image">
              <div class="image-field image-field--wide">
                <el-input v-model.trim="form.heroImage" placeholder="图片地址 Image URL" />
                <div class="image-field__actions">
                  <el-upload action="" :http-request="request => uploadImage('heroImage', request)" :show-file-list="false" accept="image/*">
                    <el-button size="small" :loading="isUploading('heroImage')">上传 Upload</el-button>
                  </el-upload>
                  <el-button size="small" @click="openMediaPicker('heroImage')">选择 Choose</el-button>
                </div>
                <el-image v-if="form.heroImage" class="image-preview" :src="assetUrl(form.heroImage)" :preview-src-list="[assetUrl(form.heroImage)]" fit="cover" />
              </div>
            </el-form-item>
          </el-tab-pane>

          <el-tab-pane label="规格 Specifications" name="specs">
            <div class="form-grid">
              <el-form-item label="木种 Species">
                <el-input v-model.trim="form.species" />
              </el-form-item>
              <el-form-item label="尺寸 Size">
                <el-input v-model.trim="form.size" />
              </el-form-item>
            </div>

            <div class="form-grid">
              <el-form-item label="表层 Top Layer">
                <el-input v-model.trim="form.topLayer" />
              </el-form-item>
              <el-form-item label="颜色 Color">
                <el-input v-model.trim="form.color" />
              </el-form-item>
            </div>

            <div class="form-grid">
              <el-form-item label="涂装 Finish">
                <el-input v-model.trim="form.finish" />
              </el-form-item>
              <el-form-item label="等级 Grading">
                <el-input v-model.trim="form.grading" />
              </el-form-item>
            </div>

            <div class="form-grid">
              <el-form-item label="表面 Surface">
                <el-input v-model.trim="form.surface" />
              </el-form-item>
              <el-form-item label="安装 Installation">
                <el-input v-model.trim="form.installation" />
              </el-form-item>
            </div>
          </el-tab-pane>

          <el-tab-pane label="描述 Content" name="content">
            <el-form-item label="摘要 Summary">
              <el-input v-model.trim="form.summary" type="textarea" :rows="2" />
            </el-form-item>

            <el-form-item label="简介 Intro">
              <el-input v-model.trim="form.intro" type="textarea" :rows="4" />
            </el-form-item>

            <el-form-item label="描述 Description">
              <el-input v-model.trim="form.description" type="textarea" :rows="5" />
            </el-form-item>
          </el-tab-pane>

          <el-tab-pane label="图库 Gallery" name="gallery">
            <el-form-item label="图库 Gallery">
              <div class="image-group-list">
                <div v-for="(url, index) in form.galleryJson" :key="index" class="image-group-item">
                  <el-input
                    :value="url"
                    placeholder="图片地址 Image URL"
                    @input="value => setGalleryUrl(index, value)"
                  />
                  <el-upload action="" :http-request="request => uploadImage(index, request)" :show-file-list="false" accept="image/*">
                    <el-button size="small" :loading="isUploading(index)">上传 Upload</el-button>
                  </el-upload>
                  <el-button size="small" @click="openMediaPicker(index)">选择 Choose</el-button>
                  <div class="order-actions">
                    <el-button size="mini" :disabled="index === 0" @click="moveGalleryItem(index, -1)">上移 Up</el-button>
                    <el-button size="mini" :disabled="index === form.galleryJson.length - 1" @click="moveGalleryItem(index, 1)">下移 Down</el-button>
                  </div>
                  <el-button size="small" type="text" class="danger-btn" @click="removeGalleryItem(index)">删除 Delete</el-button>
                  <el-image v-if="url" class="group-preview" :src="assetUrl(url)" :preview-src-list="[assetUrl(url)]" fit="cover" />
                </div>
              </div>
              <div class="gallery-actions">
                <el-button size="small" @click="addGalleryItem">添加图片 Add Image</el-button>
                <el-button size="small" @click="openMediaPicker('galleryJson')">从媒体库加入 Choose From Media</el-button>
                <el-upload action="" :http-request="request => uploadImage('galleryJson', request)" :show-file-list="false" accept="image/*">
                  <el-button size="small" :loading="isUploading('galleryJson')">上传并加入 Upload & Add</el-button>
                </el-upload>
              </div>
            </el-form-item>
          </el-tab-pane>
        </el-tabs>
      </el-form>

      <span slot="footer">
        <el-button @click="dialogVisible = false">取消 Cancel</el-button>
        <el-button type="primary" :loading="submitting" @click="handleSubmit">保存 Save</el-button>
      </span>
    </el-dialog>

    <media-picker-dialog
      :visible.sync="mediaPickerVisible"
      upload-module="product"
      @select="chooseMedia"
    />
  </div>
</template>

<script>
import {
  createProduct,
  deleteProduct,
  fetchCategories,
  fetchProductFilterOptions,
  fetchProducts,
  updateProduct
} from '../../api/products'
import { resolveAssetUrl, uploadMediaAsset } from '../../api/media'
import MediaPickerDialog from '../../components/MediaPickerDialog.vue'
import { openPreviewUrl, productPreviewUrl } from '../../utils/preview'
import { toSlug } from '../../utils/slug'

const createEmptyForm = () => ({
  id: null,
  code: '',
  title: '',
  slug: '',
  eyebrow: '',
  summary: '',
  intro: '',
  description: '',
  image: '',
  coverImage: '',
  heroImage: '',
  galleryJson: [],
  color: '',
  species: '',
  size: '',
  topLayer: '',
  surface: '',
  finish: '',
  grading: '',
  installation: '',
  status: 'active',
  sortOrder: 0,
  categoryId: ''
})

export default {
  name: 'ProductListView',
  components: {
    MediaPickerDialog
  },
  data () {
    return {
      loading: false,
      submitting: false,
      duplicatingId: null,
      dialogVisible: false,
      mediaPickerVisible: false,
      dialogMode: 'create',
      activeProductTab: 'basic',
      slugTouched: false,
      uploadingField: '',
      pickerTarget: '',
      keyword: '',
      statusFilter: '',
      categoryId: '',
      colorFilter: '',
      categories: [],
      products: [],
      filterOptions: {
        colors: []
      },
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      form: createEmptyForm(),
      rules: {
        code: [{ required: true, message: '请输入产品编号 Please input product code.', trigger: 'blur' }],
        title: [{ required: true, message: '请输入产品名称 Please input product name.', trigger: 'blur' }],
        slug: [{ required: true, message: '请输入标识 Please input slug.', trigger: 'blur' }],
        status: [{ required: true, message: '请选择状态 Please select status.', trigger: 'change' }]
      }
    }
  },
  computed: {
    colorOptions () {
      return this.filterOptions.colors
    }
  },
  watch: {
    'form.title' (value) {
      if (!this.slugTouched) {
        this.form.slug = toSlug(value || this.form.code)
      }
    },
    'form.code' (value) {
      if (!this.slugTouched && !this.form.title) {
        this.form.slug = toSlug(value)
      }
    }
  },
  async created () {
    await this.loadData(true)
  },
  methods: {
    productQueryParams () {
      return {
        page: this.pagination.page,
        pageSize: this.pagination.pageSize,
        keyword: this.keyword || undefined,
        categoryId: this.categoryId || undefined,
        status: this.statusFilter || undefined,
        color: this.colorFilter || undefined
      }
    },
    async loadData (includeBaseData = false) {
      this.loading = true
      try {
        const tasks = [fetchProducts(this.productQueryParams())]
        if (includeBaseData) {
          tasks.push(fetchCategories(), fetchProductFilterOptions())
        }
        const [productRes, categoryRes, filterRes] = await Promise.all(tasks)
        const productData = productRes.data
        this.products = Array.isArray(productData) ? productData : productData.items
        this.pagination.total = Array.isArray(productData) ? productData.length : productData.total
        if (categoryRes) this.categories = categoryRes.data
        if (filterRes) this.filterOptions = filterRes.data
      } catch (error) {
        this.$message.error('产品数据加载失败 Failed to load product data.')
      } finally {
        this.loading = false
      }
    },
    handleFilterChange () {
      this.pagination.page = 1
      this.loadData()
    },
    handlePageChange (page) {
      this.pagination.page = page
      this.loadData()
    },
    handlePageSizeChange (pageSize) {
      this.pagination.page = 1
      this.pagination.pageSize = pageSize
      this.loadData()
    },
    openCreateDialog () {
      this.dialogMode = 'create'
      this.activeProductTab = 'basic'
      this.slugTouched = false
      this.dialogVisible = true
    },
    openEditDialog (row) {
      this.dialogMode = 'edit'
      this.activeProductTab = 'basic'
      this.slugTouched = true
      this.form = {
        id: row.id,
        code: row.code || '',
        title: row.title || '',
        slug: row.slug || '',
        eyebrow: row.eyebrow || '',
        summary: row.summary || '',
        intro: row.intro || '',
        description: row.description || '',
        image: row.image || '',
        coverImage: row.coverImage || '',
        heroImage: row.heroImage || '',
        galleryJson: Array.isArray(row.galleryJson) ? row.galleryJson.slice() : this.parseGallery(row.galleryJson),
        color: row.color || '',
        species: row.species || '',
        size: row.size || '',
        topLayer: row.topLayer || '',
        surface: row.surface || '',
        finish: row.finish || '',
        grading: row.grading || '',
        installation: row.installation || '',
        status: row.status || 'draft',
        sortOrder: row.sortOrder || 0,
        categoryId: row.categoryId || ''
      }
      this.dialogVisible = true
    },
    resetForm () {
      this.form = createEmptyForm()
      this.activeProductTab = 'basic'
      this.slugTouched = false
      if (this.$refs.form) this.$refs.form.resetFields()
    },
    handleSlugInput () {
      this.slugTouched = true
      this.form.slug = toSlug(this.form.slug)
    },
    assetUrl (fileUrl) {
      return resolveAssetUrl(fileUrl)
    },
    uploadKey (field) {
      return typeof field === 'number' ? `gallery:${field}` : String(field || '')
    },
    isUploading (field) {
      return this.uploadingField === this.uploadKey(field)
    },
    parseGallery (value) {
      if (Array.isArray(value)) {
        return value.map(item => String(item || '').trim()).filter(Boolean)
      }

      return String(value || '')
        .split('\n')
        .map(item => item.trim())
        .filter(Boolean)
    },
    appendGalleryUrl (fileUrl) {
      const urls = this.parseGallery(this.form.galleryJson)
      if (!urls.includes(fileUrl)) {
        urls.push(fileUrl)
      }
      this.form.galleryJson = urls
    },
    setGalleryUrl (index, value) {
      this.$set(this.form.galleryJson, index, String(value || '').trim())
    },
    addGalleryItem () {
      this.form.galleryJson.push('')
    },
    removeGalleryItem (index) {
      this.form.galleryJson.splice(index, 1)
    },
    moveGalleryItem (index, direction) {
      const targetIndex = index + direction
      if (targetIndex < 0 || targetIndex >= this.form.galleryJson.length) return

      const nextGallery = this.form.galleryJson.slice()
      const current = nextGallery[index]
      nextGallery.splice(index, 1)
      nextGallery.splice(targetIndex, 0, current)
      this.form.galleryJson = nextGallery
    },
    openMediaPicker (field) {
      this.pickerTarget = field
      this.mediaPickerVisible = true
    },
    chooseMedia (item) {
      if (this.pickerTarget === 'galleryJson') {
        this.appendGalleryUrl(item.fileUrl)
      } else if (typeof this.pickerTarget === 'number') {
        this.setGalleryUrl(this.pickerTarget, item.fileUrl)
      } else {
        this.form[this.pickerTarget] = item.fileUrl
      }
      this.mediaPickerVisible = false
    },
    previewProduct (row) {
      openPreviewUrl(productPreviewUrl(row))
    },
    copyUniqueValue (value, fallback, maxLength) {
      const suffix = `-copy-${Date.now().toString(36)}`
      const base = String(value || fallback || 'item').trim() || 'item'
      return `${base.slice(0, Math.max(maxLength - suffix.length, 1))}${suffix}`
    },
    async uploadImage (field, { file }) {
      if (this.uploadingField) return
      this.uploadingField = this.uploadKey(field)
      try {
        const res = await uploadMediaAsset(file, 'product')
        if (field === 'galleryJson') {
          this.appendGalleryUrl(res.data.fileUrl)
        } else if (typeof field === 'number') {
          this.setGalleryUrl(field, res.data.fileUrl)
        } else {
          this.form[field] = res.data.fileUrl
        }
        this.$message.success('图片已上传 Image uploaded.')
      } catch (error) {
        this.$message.error((error.response && error.response.data && error.response.data.message) || '上传失败 Upload failed.')
      } finally {
        this.uploadingField = ''
      }
    },
    handleSubmit () {
      this.$refs.form.validate(async valid => {
        if (!valid || this.submitting) return
        this.submitting = true
        try {
          const payload = {
            ...this.form,
            galleryJson: this.parseGallery(this.form.galleryJson),
            categoryId: this.form.categoryId || null
          }
          if (this.dialogMode === 'create') {
            await createProduct(payload)
            this.$message.success('产品已创建 Product created.')
          } else {
            await updateProduct(this.form.id, payload)
            this.$message.success('产品已更新 Product updated.')
          }
          this.dialogVisible = false
          await this.loadData()
        } catch (error) {
          this.$message.error((error.response && error.response.data && error.response.data.message) || '保存失败 Save failed.')
        } finally {
          this.submitting = false
        }
      })
    },
    buildProductPayload (row, overrides = {}) {
      return {
        code: row.code,
        title: row.title,
        slug: row.slug,
        eyebrow: row.eyebrow || '',
        summary: row.summary || '',
        intro: row.intro || '',
        description: row.description || '',
        image: row.image || '',
        coverImage: row.coverImage || '',
        heroImage: row.heroImage || '',
        galleryJson: this.parseGallery(row.galleryJson),
        color: row.color || '',
        species: row.species || '',
        size: row.size || '',
        topLayer: row.topLayer || '',
        surface: row.surface || '',
        finish: row.finish || '',
        grading: row.grading || '',
        installation: row.installation || '',
        status: row.status || 'draft',
        sortOrder: row.sortOrder || 0,
        categoryId: row.categoryId || null,
        ...overrides
      }
    },
    async toggleProductStatus (row, enabled) {
      const previousStatus = row.status
      const nextStatus = enabled ? 'active' : 'draft'
      row.status = nextStatus

      try {
        await updateProduct(row.id, this.buildProductPayload(row, { status: nextStatus }))
        this.$message.success('状态已更新 Status updated.')
      } catch (error) {
        row.status = previousStatus
        this.$message.error((error.response && error.response.data && error.response.data.message) || '状态更新失败 Failed to update status.')
      }
    },
    async updateProductSortOrder (row, value) {
      const previousSortOrder = row.sortOrder || 0
      const nextSortOrder = Number(value || 0)
      row.sortOrder = nextSortOrder

      try {
        await updateProduct(row.id, this.buildProductPayload(row, { sortOrder: nextSortOrder }))
        this.$message.success('排序已更新 Sort order updated.')
      } catch (error) {
        row.sortOrder = previousSortOrder
        this.$message.error((error.response && error.response.data && error.response.data.message) || '排序更新失败 Failed to update sort order.')
      }
    },
    async handleDuplicate (row) {
      if (this.duplicatingId) return
      this.duplicatingId = row.id

      try {
        const payload = this.buildProductPayload(row, {
          code: this.copyUniqueValue(row.code, row.title, 60),
          slug: toSlug(this.copyUniqueValue(row.slug, row.code || row.title, 220)),
          status: 'draft',
          sortOrder: Number(row.sortOrder || 0) + 1
        })
        await createProduct(payload)
        this.$message.success('产品已复制为草稿 Product copied as draft.')
        await this.loadData()
      } catch (error) {
        this.$message.error((error.response && error.response.data && error.response.data.message) || '复制失败 Copy failed.')
      } finally {
        this.duplicatingId = null
      }
    },
    handleDelete (row) {
      this.$confirm(`确认删除产品“${row.title}”吗？\nConfirm deleting this product?`, '删除确认 Delete Confirmation', {
        type: 'warning'
      }).then(async () => {
        try {
          await deleteProduct(row.id)
          this.$message.success('产品已删除 Product deleted.')
          await this.loadData()
        } catch (error) {
          this.$message.error((error.response && error.response.data && error.response.data.message) || '删除失败 Delete failed.')
        }
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.toolbar-card,
.table-card {
  border: 1px solid var(--line);
  border-radius: 12px;
  background: var(--panel);
  box-shadow: 0 16px 32px rgba(23, 20, 17, 0.04);
}

.toolbar-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 22px 24px;
}

.toolbar-card h2 {
  margin: 0;
  font-size: 22px;
  font-weight: 500;
}

.toolbar-card p {
  margin: 8px 0 0;
  color: var(--muted);
}

.toolbar-card__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.toolbar-card__actions .el-input,
.toolbar-card__actions .el-select {
  width: 190px;
}

.table-card {
  margin-top: 18px;
  padding: 12px;
}

.pagination-bar {
  display: flex;
  justify-content: flex-end;
  padding: 14px 4px 2px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.sort-input {
  width: 112px;
}

.product-form-tabs {
  margin-top: -8px;
}

.product-form-tabs ::v-deep .el-tabs__content {
  overflow: visible;
  padding-top: 16px;
}

.danger-btn {
  color: #d14343;
}

.image-field {
  display: grid;
  gap: 10px;
}

.image-field--wide {
  max-width: 420px;
}

.image-field__actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.image-preview {
  display: block;
  width: 100%;
  max-width: 220px;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: #f2eee8;
}

.image-group-list {
  display: grid;
  gap: 12px;
}

.image-group-item {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) auto auto auto auto 96px;
  gap: 10px;
  align-items: center;
  padding: 10px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: #fbfaf7;
}

.group-preview {
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: #f2eee8;
}

.gallery-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 12px;
}

.order-actions {
  display: inline-flex;
  gap: 6px;
}

.picker-toolbar {
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
}

.picker-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
  min-height: 260px;
}

.picker-card {
  overflow: hidden;
  padding: 0;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: var(--panel);
  text-align: left;
  cursor: pointer;
}

.picker-card .el-image {
  display: block;
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  background: #f2eee8;
}

.picker-card span {
  display: block;
  overflow: hidden;
  padding: 9px 10px;
  color: var(--text);
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 960px) {
  .toolbar-card,
  .form-grid {
    grid-template-columns: 1fr;
  }

  .toolbar-card {
    flex-direction: column;
    align-items: flex-start;
  }

  .toolbar-card__actions {
    width: 100%;
    flex-wrap: wrap;
  }

  .image-group-item {
    grid-template-columns: 1fr;
  }
}
</style>
